// Tutor-side BMD report dashboard for a single student.
//
// URL: /bmd-report/:student_key
// Year level lives in the BMD Base Level dropdown on the header strip; the
// tab is transient state. Tabs: Main Report, Mastery Breakdown, Chain
// Analysis, Skills Gap, Remediation.

import React from "react"
import { Container } from "reactstrap"
import { useNavigate, useParams } from "react-router-dom"
import {
  getMyStudentsForReport,
  getStudentReport,
} from "../../modules/bmd/services/bmd-report.service"
import type {
  BmdAttemptHistoryRow,
  BmdReportStudent,
  GetStudentReportResponse,
} from "../../modules/bmd/types/bmd-report.types"
import { BmdReportKpiCards } from "../../modules/bmd/components/BmdReportKpiCards"
import { BmdTopicStrengthRadar } from "../../modules/bmd/components/BmdTopicStrengthRadar"
import { BmdAttemptHistoryTable } from "../../modules/bmd/components/BmdAttemptHistoryTable"
import { BmdInterventionFlagsPanel } from "../../modules/bmd/components/BmdInterventionFlagsPanel"
import { BmdMasteryBreakdownTab } from "../../modules/bmd/components/BmdMasteryBreakdownTab"
import { BmdAttemptReviewModal } from "../../modules/bmd/components/BmdAttemptReviewModal"
import { BmdDashboardHeader } from "../../modules/bmd/components/BmdDashboardHeader"
import {
  BmdDashboardTabs,
  type BmdDashboardTabKey,
} from "../../modules/bmd/components/BmdDashboardTabs"
import {
  BmdChainAnalysisTab,
  type ChainTopicOption,
} from "../../modules/bmd/components/BmdChainAnalysisTab"
import { BmdSkillsGapTab } from "../../modules/bmd/components/BmdSkillsGapTab"
import { BmdSbRemediationTab } from "../../modules/bmd/components/BmdSbRemediationTab"
import { EngDiagAttemptReviewModal } from "../../modules/eng-diag/components/EngDiagAttemptReviewModal"
import {
  getEngStudentReport,
  toBmdReportShape,
  ENG_REPORT_FETCHERS,
} from "../../modules/eng-diag/services/eng-report.service"
import {
  ReportFetchersProvider,
  MATH_FETCHERS,
  type ReportFetchers,
} from "../../modules/bmd/services/report-fetchers"
import {
  ReportCopyProvider,
  MATH_COPY,
  ENGLISH_COPY,
} from "../../modules/bmd/services/report-copy"

// The two subjects this dashboard can show. English is a separate backend behind the same
// components - see eng-report.service.ts for why - so the page picks a fetcher here and
// everything below renders identically.
const SUBJECTS = [
  { key: "math", label: "Mathematics" },
  { key: "english", label: "English" },
] as const

type SubjectKey = (typeof SUBJECTS)[number]["key"]

/**
 * Tabs English does not have.
 *
 * Only "sb": there are no English Skill Builders to remediate with, so the Remediation tab has
 * nothing to list. Chain Analysis WAS hidden here until the English topic-detail endpoint
 * learned to return `theoretical_prereq_chain`; it reads English now.
 */
const ENGLISH_HIDDEN_TABS: BmdDashboardTabKey[] = ["sb"]

// Remembered like the session scope, and for the same reason: a tutor who works in one subject
// wants the report to open there every time, not to reset to maths on every visit.
const SUBJECT_STORAGE_KEY = "diagReportSubject"

// Dedupes by topic_name, keeping the latest-attempted session_topic per topic.
// This mirrors the latest-per-topic rule the backend uses for the KPIs and the
// radar, so the Chain Analysis tab's "All topics" view stays in sync with the
// rest of the dashboard.
function buildChainTopicOptions(
  attempts: BmdAttemptHistoryRow[],
): ChainTopicOption[] {
  const latestByName = new Map<string, BmdAttemptHistoryRow>()
  for (const a of attempts) {
    const key = a.topic_name || "—"
    const existing = latestByName.get(key)
    const t = a.attempted_on ? new Date(a.attempted_on).getTime() : 0
    const existingT =
      existing && existing.attempted_on
        ? new Date(existing.attempted_on).getTime()
        : 0
    if (!existing || t > existingT) latestByName.set(key, a)
  }
  return Array.from(latestByName.values()).map((a) => ({
    pk_session_topic_key: a.pk_session_topic_key,
    topic_name: a.topic_name,
    strand_id: a.strand_id ?? null,
    strand_name: a.strand_name ?? "",
    year_level_name: a.level_name,
  }))
}

function mostRecentAttemptDate(attempts: BmdAttemptHistoryRow[]): string | null {
  if (attempts.length === 0) return null
  let latest: number = -Infinity
  for (const a of attempts) {
    const t = a.attempted_on ? new Date(a.attempted_on).getTime() : NaN
    if (!Number.isNaN(t) && t > latest) latest = t
  }
  if (latest === -Infinity) return null
  return new Date(latest).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  })
}

// One key for the whole dashboard: the scope is a tutor preference, not a
// per-student setting.
const REPORT_SCOPE_STORAGE_KEY = "bmdReportSessionScope"

const BmdReportDetailPage: React.FC = () => {
  const { key: studentKey } = useParams<{ key: string }>()
  const navigate = useNavigate()
  const [report, setReport] = React.useState<GetStudentReportResponse | null>(
    null,
  )
  const [loading, setLoading] = React.useState<boolean>(false)
  const [error, setError] = React.useState<string | null>(null)
  const [yearLevelId, setYearLevelId] = React.useState<number | undefined>(
    undefined,
  )
  const [previewTopicKey, setPreviewTopicKey] = React.useState<string | null>(
    null,
  )
  // Report scope. null means the whole year level, which is what the dashboard
  // did before this existed. Neither mode replaces the other, so it is
  // remembered as a tutor preference and deliberately not keyed per student: a
  // tutor who prefers session-by-session wants that everywhere. It clears
  // itself when the stored session is not valid for the student and year on
  // screen.
  const [sessionKey, setSessionKey] = React.useState<string | null>(() => {
    try {
      return localStorage.getItem(REPORT_SCOPE_STORAGE_KEY) || null
    } catch {
      return null
    }
  })
  const changeScope = React.useCallback((key: string | null) => {
    setSessionKey(key)
    try {
      if (key) localStorage.setItem(REPORT_SCOPE_STORAGE_KEY, key)
      else localStorage.removeItem(REPORT_SCOPE_STORAGE_KEY)
    } catch {
      // Private browsing or storage disabled. Scope still works for this view.
    }
  }, [])
  const [subject, setSubject] = React.useState<SubjectKey>(() => {
    try {
      const stored = localStorage.getItem(SUBJECT_STORAGE_KEY)
      return stored === "english" ? "english" : "math"
    } catch {
      return "math"
    }
  })
  const changeSubject = React.useCallback((key: string) => {
    const next: SubjectKey = key === "english" ? "english" : "math"
    setSubject(next)
    // A tab the new subject does not have would otherwise keep rendering its body while the
    // strip above no longer offers it - switching from maths Chain Analysis to English left the
    // tutor looking at a tab that cannot load.
    setActiveTab(prev =>
      next === "english" && ENGLISH_HIDDEN_TABS.includes(prev) ? "main" : prev,
    )
    // The year levels and sessions belong to the subject that was on screen, so carrying them
    // across would ask the new backend for a year it has never heard of. Cleared, and the
    // response picks the student's most recent year in that subject.
    setYearLevelId(undefined)
    setReport(null)
    try {
      localStorage.setItem(SUBJECT_STORAGE_KEY, next)
    } catch {
      // Private browsing or storage disabled. The choice still holds for this visit.
    }
  }, [])

  // Tutor's full student list for the in-header student switcher. Fetched
  // once and reused as the tutor jumps between students without bouncing
  // back to /bmd-reports.
  const [students, setStudents] = React.useState<BmdReportStudent[]>([])

  React.useEffect(() => {
    const controller = new AbortController()
    getMyStudentsForReport(controller.signal)
      .then((resp) => setStudents(resp.students))
      .catch(() => {
        // Soft-fail: header just falls back to the static student name.
      })
    return () => controller.abort()
  }, [])

  const [activeTab, setActiveTab] = React.useState<BmdDashboardTabKey>("main")
  // Topic preselection for the Chain Analysis tab when entered via the
  // "Chain" button on a Main Report row. Cleared whenever the user leaves
  // the chain tab so re-entry via the nav defaults back to "All topics".
  const [chainInitialTopicKey, setChainInitialTopicKey] = React.useState<
    string | null
  >(null)

  React.useEffect(() => {
    if (activeTab !== "chain") {
      setChainInitialTopicKey(null)
    }
  }, [activeTab])

  React.useEffect(() => {
    if (!studentKey) return
    const controller = new AbortController()
    setLoading(true)
    setError(null)
    const request = {
      student_key: studentKey,
      year_level_id: yearLevelId,
      diagnostics_session_key: sessionKey,
    }
    // One promise either way, so everything downstream is subject-blind. English comes back in
    // its own shape and is adapted on the way through; every number the page draws is on the
    // adapted shape, so there is no second copy of the payload to keep in step.
    const load: Promise<GetStudentReportResponse> =
      subject === "english"
        ? getEngStudentReport(request, controller.signal).then(toBmdReportShape)
        : getStudentReport(request, controller.signal)

    load
      .then((data) => {
        if (controller.signal.aborted) return
        setReport(data)
        if (yearLevelId == null && data.selected_year_level) {
          setYearLevelId(data.selected_year_level.year_level_id)
        }
        // The remembered session belongs to a different student or year:
        // fall back to the full year level rather than showing an empty report.
        if (
          sessionKey &&
          data.available_sessions.length > 0 &&
          !data.available_sessions.some(
            (x) => x.diagnostics_session_key === sessionKey,
          )
        ) {
          changeScope(null)
        }
        setLoading(false)
      })
      .catch((err) => {
        if (controller.signal.aborted) return
        setError(err?.message || "Failed to load report")
        setLoading(false)
      })
    return () => controller.abort()
  }, [studentKey, yearLevelId, sessionKey, subject])

  if (!studentKey) {
    return (
      <div className="page-content">
        <Container fluid>
          <p className="text-danger">Missing student key.</p>
        </Container>
      </div>
    )
  }

  // The tabs that fetch their own data read these from context rather than importing a service
  // directly, so switching subject re-points all of them at once.
  const fetchers = React.useMemo<ReportFetchers>(
    () => (subject === "english" ? ENG_REPORT_FETCHERS : MATH_FETCHERS),
    [subject],
  )
  // The words that differ between the subjects. Read here for the page's own empty states, and
  // handed to the provider for the components that have their own.
  const copy = subject === "english" ? ENGLISH_COPY : MATH_COPY

  const sessionDate = report ? mostRecentAttemptDate(report.attempts) : null
  // Memoize so the chain tab's effect doesn't refire on every parent render.
  const chainTopicOptions = React.useMemo<ChainTopicOption[]>(
    () => (report ? buildChainTopicOptions(report.attempts) : []),
    [report],
  )
  // TODO: derive from session.flow_status once the report response carries a
  // session-level status. Hardcoded to "In Progress" until then.
  const statusLabel = "In Progress"
  const statusVariant: "success" | "secondary" | "warning" = "success"

  return (
    <ReportFetchersProvider value={fetchers}>
    <ReportCopyProvider value={copy}>
    <div className="page-content">
      <Container fluid>
        <button
          type="button"
          className="btn btn-link text-dark fw-bold p-0 mb-2"
          onClick={() => navigate("/bmd-reports")}
        >
          <i className="fas fa-chevron-left me-1"></i>
          Back
        </button>

        {loading && !report ? (
          <p className="text-muted mb-0">Loading report…</p>
        ) : error ? (
          <p className="text-danger mb-0">{error}</p>
        ) : !report ? null : (
          <>
            <BmdDashboardHeader
              student={report.student}
              students={students}
              onStudentChange={(key) => {
                if (key && key !== studentKey) {
                  // Reset the year level so the destination student picks its
                  // own most recent one. Otherwise the previous student's
                  // selection bleeds across.
                  setYearLevelId(undefined)
                  navigate(`/bmd-report/${key}`)
                }
              }}
              availableYearLevels={report.available_year_levels}
              selectedYearLevel={report.selected_year_level}
              onYearLevelChange={(id) => setYearLevelId(id)}
              sessionDate={sessionDate}
              sessions={report.available_sessions}
              selectedSessionKey={report.selected_session_key}
              onSessionChange={changeScope}
              subjects={SUBJECTS as unknown as { key: string; label: string }[]}
              selectedSubject={subject}
              onSubjectChange={changeSubject}
              baseLevelLabel={
                subject === "english" ? "English Base Level" : "BMD Base Level"
              }
              statusLabel={statusLabel}
              statusVariant={statusVariant}
            />
            <BmdDashboardTabs
              active={activeTab}
              onChange={(key) => setActiveTab(key)}
              hiddenKeys={subject === "english" ? ENGLISH_HIDDEN_TABS : []}
            />

            {activeTab === "main" &&
              (report.selected_year_level ? (
                <>
                  <BmdReportKpiCards
                    kpi={report.kpi}
                    kpiOverall={report.kpi_overall}
                  />
                  <BmdTopicStrengthRadar
                    masteryTree={report.mastery_tree ?? []}
                    yearLevelName={report.selected_year_level.level_name}
                  />
                  <BmdAttemptHistoryTable
                    attempts={report.attempts}
                    onPreview={(row) =>
                      setPreviewTopicKey(row.pk_session_topic_key)
                    }
                    onViewChainAnalysis={(row) => {
                      // Remap to the latest-per-topic key so the chain tab's
                      // dropdown selection matches topicOptions, which is
                      // deduped by topic_name. Falls back to the row's own key
                      // when there is no match.
                      const latestKey = row
                        ? chainTopicOptions.find(
                            (o) => o.topic_name === row.topic_name,
                          )?.pk_session_topic_key ?? row.pk_session_topic_key
                        : null
                      setChainInitialTopicKey(latestKey)
                      setActiveTab("chain")
                    }}
                  />
                  {/* Mastery by Skill Area used to sit here. It moved to the
                      Mastery Breakdown tab, where it can be drilled into. */}
                  {/* English raises no intervention flags at all - there is no edt_ twin of
                      dt_diagnostics_intervention_flag and no trigger has been specified - so
                      the panel is not drawn rather than standing there permanently saying
                      "No active intervention flags". For maths an empty list is news: it
                      means nothing is outstanding. */}
                  {subject === "english" ? null : (
                    <BmdInterventionFlagsPanel
                      flags={report.intervention_flags}
                    />
                  )}
                </>
              ) : (
                <p className="text-muted">{copy.noDataForStudent}</p>
              ))}

            {activeTab === "mastery" &&
              (report.selected_year_level ? (
                <BmdMasteryBreakdownTab
                  masteryTree={report.mastery_tree ?? []}
                  baseYearLabel={report.selected_year_level.level_name}
                />
              ) : (
                <p className="text-muted">{copy.noDataForStudent}</p>
              ))}

            {activeTab === "chain" && (
              <BmdChainAnalysisTab
                topicOptions={chainTopicOptions}
                initialTopicKey={chainInitialTopicKey}
                studentName={report.student.name}
                baseYearLevelName={
                  report.selected_year_level?.level_name ?? null
                }
                baseYearLevelId={
                  report.selected_year_level?.year_level_id ?? null
                }
                diagnosticsSessionKey={sessionKey}
                studentKey={studentKey}
              />
            )}
            {activeTab === "skills" && (
              <BmdSkillsGapTab
                studentKey={studentKey}
                yearLevelId={
                  report.selected_year_level?.year_level_id ?? undefined
                }
                // The tab has always accepted this and never been given it, so its table
                // counted the whole year level while the KPI strip inside it - fed from the
                // scoped `kpi` below - counted the selected diagnostic.
                diagnosticsSessionKey={sessionKey}
                baseYearLevelName={
                  report.selected_year_level?.level_name ?? null
                }
                studentName={report.student.name}
                kpi={report.kpi}
                kpiOverall={report.kpi_overall}
              />
            )}
            {activeTab === "sb" && (
              <BmdSbRemediationTab
                studentKey={studentKey}
                diagnosticsSessionKey={sessionKey}
              />
            )}
          </>
        )}

        {/* Two modals rather than one with a flag: an English review is keyed on the ANSWER,
            because a question can be marked in several parts, and the maths modal renders one
            choice from one answer list. See EngDiagAttemptReviewModal for the full reason. */}
        {subject === "english" ? (
          <EngDiagAttemptReviewModal
            isOpen={previewTopicKey !== null}
            onClose={() => setPreviewTopicKey(null)}
            sessionTopicKey={previewTopicKey}
          />
        ) : (
          <BmdAttemptReviewModal
            isOpen={previewTopicKey !== null}
            onClose={() => setPreviewTopicKey(null)}
            sessionTopicKey={previewTopicKey}
          />
        )}
      </Container>
    </div>
    </ReportCopyProvider>
    </ReportFetchersProvider>
  )
}

export default BmdReportDetailPage
